import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PresetDetailView } from "./PresetDetailView";
import { usePolicies, Policy } from "@/hooks/usePolicies";
import { CreatePolicyDialog } from "./CreatePolicyDialog";
import { format } from "date-fns";

export const PresetsSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  
  const { policies, isLoading, createPolicy } = usePolicies();

  useEffect(() => {
    const policyId = searchParams.get('policyId');
    if (policyId && policies) {
      const policy = policies.find(p => p._id === policyId);
      if (policy) {
        setSelectedPolicy(policy);
      }
    }
  }, [policies, searchParams]);

  const handlePolicySelect = (policy: Policy) => {
    setSelectedPolicy(policy);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('policyId', policy._id);
    setSearchParams(newSearchParams);
  };

  const handleBackToList = () => {
    setSelectedPolicy(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('policyId');
    setSearchParams(newSearchParams);
  };

  const handleCreatePolicy = async (data: { name: string; description: string }) => {
    await createPolicy(data);
    setIsCreateDialogOpen(false);
  };

  if (selectedPolicy) {
    return (
      <PresetDetailView preset={selectedPolicy} onBack={handleBackToList} />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-6 pb-6">
      <div className="flex items-start">
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Policy
        </Button>
      </div>

      {policies && policies.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Settings className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No policies created yet</h3>
            <p className="text-gray-500 text-center mb-6 max-w-md">
              Create your first browser policy to start applying standardized configurations across devices and groups.
            </p>
            <Button 
              onClick={() => setIsCreateDialogOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Policy
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy Name</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies?.map(policy => (
                <TableRow 
                  key={policy._id}
                  className="cursor-pointer hover:bg-muted/50" 
                  onClick={() => handlePolicySelect(policy)}
                >
                  <TableCell>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                        <Settings className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{policy.name}</div>
                        <div className="text-sm text-gray-500">{policy.description}</div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreatePolicyDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreatePolicy={handleCreatePolicy}
      />
    </div>
  );
};